import type { VitalsBucket } from '@ctw/shared/api/fhir/models/vitals-bucket';
import { Button } from '@ctw/shared/components/button';
import { Heading } from '@ctw/shared/components/heading';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { notify } from '@ctw/shared/components/toast';
import { useVitalWriteback } from '@ctw/shared/content/vitals/helpers/vital-write';
import { VitalsList } from '@ctw/shared/content/vitals/helpers/vitals-list';
import type { ModalState } from '@ctw/shared/context/modal-context';
import { useModal } from '@ctw/shared/context/modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useWritebacks } from '@ctw/shared/context/writeback-provider';
import { tw } from '@ctw/shared/utils/tailwind';
import { useEffect, useState } from 'react';

interface VitalModalProps {
  vitalBucket: VitalsBucket;
  onEvent: ModalState['onModalEvent'];
  close: () => void;
}

const VitalModal = ({ vitalBucket, onEvent, close }: VitalModalProps) => {
  const [_error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const vitalWriteback = useVitalWriteback();
  const { onWritebackStart, onWritebackSuccess, onWritebackFailure } = useWritebacks();

  useEffect(() => onEvent('open', () => setError(null)), [onEvent]);

  const handleAddToChart = async () => {
    setError(null);
    setIsSubmitting(true);

    onWritebackStart(vitalBucket.key);

    close();

    try {
      const response = await vitalWriteback(vitalBucket);

      onWritebackSuccess(vitalBucket.key, response);

      notify({
        type: 'success',
        title: 'Successfully added to chart',
        body: `Vitals taken on ${vitalBucket.date} have been added to the patient's chart`,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';

      notify({
        type: 'error',
        title: 'Error adding vitals to chart',
        body: errorMessage,
      });

      onWritebackFailure(vitalBucket.key, errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={tw`space-y-4`}>
      <Heading level="h1">Add to chart</Heading>

      <p className={tw`text-gray-700`}>
        The following data will be imported to the chart for: Vitals taken on {vitalBucket.date}
      </p>

      <VitalsList vitalBucket={vitalBucket} />

      <div className={tw`flex justify-end gap-3 pt-4`}>
        <Button type="button" variant="secondary" onClick={close} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={() => void handleAddToChart()}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className={tw`mr-2`}>Adding...</span>
              <LoadingSpinner />
            </>
          ) : (
            'Add to Chart'
          )}
        </Button>
      </div>
    </div>
  );
};

export function useVitalModal() {
  const telemetry = useTelemetry();
  const { openModal, onModalEvent, closeModal } = useModal({
    boundaryName: 'VitalModal',
  });

  return (vitalBucket: VitalsBucket) => {
    openModal({
      telemetry,
      action: {
        content: {
          body: <VitalModal onEvent={onModalEvent} close={closeModal} vitalBucket={vitalBucket} />,
        },
      },
    });
  };
}
